import { Reader } from './live-codec.js';
import { readResource, resourceValue, resourceProperties } from './vgui-res.js';

export function decodeVoice(payload) {
    const r = new Reader(payload), sequence = r.u32(), reset = r.u8(), count = r.u8();
    if (!sequence || reset > 1 || count > 128) throw new Error('Invalid voice header');
    const speakers = [];
    for (let i = 0; i < count; i++) {
        const index = r.u32(), generation = r.u32();
        if (!index || index > 128 || !generation) throw new Error('Invalid voice speaker');
        speakers.push(`${index}:${generation}`);
    }
    const packet = r.take(r.u16());
    if (!packet.length || packet.length > 1275) throw new Error('Invalid Opus packet');
    r.end(); return { sequence, reset: !!reset, speakers, packet };
}

const config = { codec: 'opus', sampleRate: 48000, numberOfChannels: 1 };
/** Bounded native Opus decoding. No audio is persisted, replayed, or attached to snapshots. */
export class LiveVoice {
    constructor({ output, root, players, cache, diagnostic = () => {} }) {
        Object.assign(this, { output, root, players, cache, diagnostic });
        this.enabled = true; this.supported = false; this.version = 0; this.active = new Map();
        this.removeStateListener = output.onStateChange?.(state => { if (state !== 'running') this.reset(); });
        this.expiry = setInterval(() => this.render(), 100);
    }
    async initialize() {
        try { this.supported = !!globalThis.AudioDecoder && !!globalThis.AudioWorkletNode && (await AudioDecoder.isConfigSupported(config)).supported; }
        catch { this.supported = false; }
        this.output.setVoiceEnabled?.(this.supported && this.enabled);
        return this.supported;
    }
    async prepare() {
        if (!this.supported || !this.output.context || this.node) return;
        if (this.preparing) return this.preparing;
        this.preparing = (async () => {
            await this.output.context.audioWorklet.addModule('/dist/voice-worklet.js');
            this.node = new AudioWorkletNode(this.output.context, 'live-voice', { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [1] });
            this.node.connect(this.output.voiceMaster);
            this.node.port.onmessage = ({ data }) => {
                if (this.metricsEnabled && data.type === 'metrics') this.bufferMetrics = data;
            };
            this.node.port.postMessage({ metrics: !!this.metricsEnabled });
        })().catch(error => { this.supported = false; this.diagnostic(`Voice playback unavailable: ${error.message}`); })
            .finally(() => { this.preparing = null; });
        return this.preparing;
    }
    setMetricsEnabled(enabled) {
        this.metricsEnabled = enabled; this.bufferMetrics = null;
        this.node?.port.postMessage({ metrics: enabled });
    }
    reset() {
        this.version++; if (this.decoder && this.decoder.state !== 'closed') this.decoder.close(); this.decoder = null; this.sequence = null;
        this.node?.port.postMessage({ reset: true }); this.active.clear(); this.render();
    }
    receive(payload) {
        const metrics = this.metrics?.(), started = metrics ? performance.now() : 0;
        try { this.receivePacket(payload); }
        finally { metrics?.record('voice', performance.now() - started); }
    }
    receivePacket(payload) {
        this.receivedPackets = (this.receivedPackets ?? 0) + 1; this.lastReceivedAt = performance.now();
        this.output.recover?.();
        if (!this.enabled || !this.supported || !this.output.allowed || this.output.context?.state !== 'running' || !this.node) return;
        const voice = decodeVoice(payload);
        if (voice.reset || (this.sequence != null && voice.sequence !== this.sequence + 1)) this.reset();
        this.sequence = voice.sequence;
        if (!this.decoder) {
            const version = this.version;
            this.decoder = new AudioDecoder({
                output: data => {
                    const metrics = this.metrics?.(), started = metrics ? performance.now() : 0;
                    try {
                        if (version !== this.version) return;
                        const samples = new Float32Array(data.numberOfFrames);
                        data.copyTo(samples, { planeIndex: 0, format: 'f32-planar' });
                        this.lastDecodedAt = performance.now();
                        this.node.port.postMessage({ samples }, [samples.buffer]);
                    } finally { data.close(); metrics?.record('voice', performance.now() - started); }
                },
                error: () => { if (version === this.version) this.reset(); },
            });
            this.decoder.configure(config);
        }
        if (this.decoder.decodeQueueSize >= 8) { this.reset(); return; }
        this.decoder.decode(new EncodedAudioChunk({ type: 'key', timestamp: voice.sequence * 20000, duration: 20000, data: voice.packet }));
        for (const key of voice.speakers) this.active.set(key, performance.now() + 300);
        this.render();
    }
    async load(assetSet) {
        if (!assetSet || assetSet === this.assetSet) return;
        this.assetSet = assetSet; this.abort?.abort(); const abort = this.abort = new AbortController();
        try {
            const entries = await readResource('scripts/hudlayout.res', path => this.cache.fetch(`/assets/${path}?assetSet=${encodeURIComponent(assetSet)}`, abort.signal));
            if (abort.signal.aborted) return;
            const hud = resourceValue(entries, 'hudvoicestatus') ?? resourceValue(entries.find(e => Array.isArray(e.value))?.value, 'hudvoicestatus');
            const props = resourceProperties(hud);
            // HudVoiceStatus draws these rows in code; there is no per-speaker .res panel.
            const size = (name, fallback, min, max) => Math.max(min, Math.min(max, Number.parseFloat(props[name]) || fallback));
            this.root.style.width = `${size('item_wide', 160, 100, 320)}px`;
            this.root.style.setProperty('--voice-row-height', `${size('item_tall', 16, 16, 48)}px`);
            this.root.style.gap = `${size('item_spacing', 2, 0, 16)}px`;
        } catch (error) { if (!abort.signal.aborted) this.diagnostic(`Voice HUD layout unavailable: ${error.message}`); }
    }
    render() {
        const metrics = this.metrics?.(), started = metrics ? performance.now() : 0;
        try { this.renderHud(); }
        finally { metrics?.record('hud', performance.now() - started); }
    }
    renderHud() {
        const now = performance.now();
        for (const [key, expiry] of this.active) if (expiry < now) this.active.delete(key);
        this.root.replaceChildren();
        for (const key of [...this.active.keys()].slice(-12)) {
            const player = this.players.get(key);
            if (!player) continue;
            const row = document.createElement('div'); row.className = `live-voice-row live-voice-team-${player.team}`;
            row.textContent = `◖)) ${player.name}`; this.root.append(row);
        }
        this.root.hidden = !this.root.childElementCount;
    }
    dispose() { this.removeStateListener?.(); this.reset(); clearInterval(this.expiry); this.abort?.abort(); this.node?.disconnect(); }
}
